import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import {
  ChartComponentProps,
  getBaseOption,
  formatNumberShort,
  formatTooltipValue,
  ECHARTS_OPTS,
  FALLBACK_PALETTE,
} from './chartConfig';

export const KPISparklineChart: React.FC<ChartComponentProps> = ({ chart, height = 160, colors }) => {
  const option = useMemo((): EChartsOption => {
    const data = chart.data || [];
    if (data.length === 0) return {};

    const base = getBaseOption(colors);
    const palette = colors && colors.length > 0 ? colors : FALLBACK_PALETTE;
    const mainColor = palette[0];

    const values = data.map(d => Number(d.value ?? 0));
    const categories = data.map(d => String(d.name ?? ''));
    const latestValue = values[values.length - 1] ?? 0;
    const prevValue = values.length >= 2 ? values[values.length - 2] : latestValue;
    const changePercent = prevValue !== 0 ? ((latestValue - prevValue) / Math.abs(prevValue)) * 100 : 0;
    const trendUp = changePercent >= 0;

    const title = chart.title || '';

    return {
      ...base,
      grid: { top: 50, right: 12, bottom: 24, left: 12, containLabel: false },
      title: {
        text: formatNumberShort(latestValue),
        subtext: `${trendUp ? '▲' : '▼'} ${Math.abs(changePercent).toFixed(1)}%`,
        left: 12,
        top: 4,
        textStyle: { fontSize: 22, color: '#1F1A20' },
        subtextStyle: { fontSize: 12, color: trendUp ? '#3F7D4F' : '#B23A48' },
      },
      xAxis: {
        type: 'category',
        data: categories,
        show: false,
        boundaryGap: false,
      },
      yAxis: {
        type: 'value',
        show: false,
      },
      tooltip: {
        ...(base.tooltip as object),
        trigger: 'axis',
        formatter: (params: unknown) => {
          const items = Array.isArray(params) ? params : [params];
          const p = items[0] as { name?: string; value?: number };
          return `<div style="font-size:12px"><b>${p.name}</b><br/>${formatTooltipValue(p.value)}</div>`;
        },
      },
      series: [
        {
          type: 'line',
          data: values,
          smooth: true,
          symbol: 'none',
          lineStyle: { width: 2.5, color: mainColor },
          areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: mainColor + '30' }, { offset: 1, color: mainColor + '05' }] } as any },
        },
      ],
      legend: { show: false },
    };
  }, [chart, colors]);

  return <ReactECharts option={option} style={{ height }} notMerge lazyUpdate opts={ECHARTS_OPTS} />;
};
